/**
 * Dynamic block lookup table + block properties table mutations (DYN-2 B.2).
 *
 * These mutations modify a BlockDefinition's `dynamicMetadata.lookupTables`
 * array and `dynamicMetadata.blockPropertiesTable` object respectively.
 * Every mutation returns the updated DrawingDocument and a human-readable
 * summary. Follows the same pattern as dynamicAuthoringMutations.ts.
 *
 * Public surface:
 *   - createLookupTableMutation
 *   - updateLookupTableMutation
 *   - deleteLookupTableMutation (rejects if a LookupParameter references it)
 *   - setBlockPropertiesTableMutation
 *   - addBlockPropertyMutation
 *   - updateBlockPropertyMutation
 *   - removeBlockPropertyMutation
 *   - reorderBlockPropertiesMutation
 *
 * All mutations enforce:
 *   - the target block must exist in `document.blocks`;
 *   - `dynamicMetadata` is lazily created with version=1 defaults when a
 *     mutation is applied for the first time;
 *   - `validateDynamicMetadata` is run after every write and any semantic
 *     error aborts the mutation (throws).
 *
 * BEDIT gating is NOT enforced here — the reducer is expected to check
 * that the active space is `block-edit` and that the block name matches
 * the active BEDIT target before invoking these helpers.
 */

import type {
  BlockDefinition,
  BlockPropertiesTable,
  BlockPropertyEntry,
  DrawingDocument,
  DynamicBlockMetadata,
  LookupParameter,
  LookupTable,
} from "@dxf-ai-editor/drawing-model";
import {
  dynamicBlockMetadataSchema,
  validateDynamicMetadata,
} from "@dxf-ai-editor/drawing-model";

import type { MutationResult } from "./dynamicAuthoringMutations";
import { requireBlock, withBlockReplace } from "./mutationHelpers";

// ═══════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════

function ensureMetadata(blockDef: BlockDefinition): DynamicBlockMetadata {
  if (blockDef.dynamicMetadata) return blockDef.dynamicMetadata;
  return dynamicBlockMetadataSchema.parse({});
}

function validateAndCommit(
  document: DrawingDocument,
  blockDef: BlockDefinition,
  nextMeta: DynamicBlockMetadata,
  summary: string,
): MutationResult {
  const parsed = dynamicBlockMetadataSchema.parse(nextMeta);
  const errors = validateDynamicMetadata(parsed);
  if (errors.length > 0) {
    throw new Error(`Invalid dynamic metadata: ${errors.join("; ")}`);
  }
  const nextBlockDef: BlockDefinition = {
    ...blockDef,
    dynamicMetadata: parsed,
  };
  return {
    updatedDocument: withBlockReplace(document, blockDef.name, () => nextBlockDef),
    summary,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// Lookup tables
// ═══════════════════════════════════════════════════════════════════════

export interface CreateLookupTableInput {
  document: DrawingDocument;
  blockName: string;
  table: LookupTable;
}

export function createLookupTableMutation(
  input: CreateLookupTableInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  if (meta.lookupTables.some((t) => t.id === input.table.id)) {
    throw new Error(`Lookup table id '${input.table.id}' already exists`);
  }
  if (
    input.table.name &&
    meta.lookupTables.some((t) => t.name === input.table.name)
  ) {
    throw new Error(`Lookup table name '${input.table.name}' already exists`);
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    lookupTables: [...meta.lookupTables, input.table],
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Added lookup table '${input.table.name || input.table.id}' to block '${input.blockName}'`,
  );
}

export interface UpdateLookupTableInput {
  document: DrawingDocument;
  blockName: string;
  tableId: string;
  updates: Partial<LookupTable>;
}

export function updateLookupTableMutation(
  input: UpdateLookupTableInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.lookupTables.find((t) => t.id === input.tableId);
  if (!existing) {
    throw new Error(`Lookup table '${input.tableId}' not found`);
  }
  // Id is immutable; merge everything else.
  const merged: LookupTable = {
    ...existing,
    ...input.updates,
    id: existing.id,
  };
  // Guard name uniqueness when the caller renames the table.
  if (
    merged.name &&
    merged.name !== existing.name &&
    meta.lookupTables.some(
      (t) => t.id !== existing.id && t.name === merged.name,
    )
  ) {
    throw new Error(`Lookup table name '${merged.name}' already exists`);
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    lookupTables: meta.lookupTables.map((t) =>
      t.id === input.tableId ? merged : t,
    ),
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Updated lookup table '${merged.name || merged.id}' in block '${input.blockName}'`,
  );
}

export interface DeleteLookupTableInput {
  document: DrawingDocument;
  blockName: string;
  tableId: string;
}

export function deleteLookupTableMutation(
  input: DeleteLookupTableInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const removed = meta.lookupTables.find((t) => t.id === input.tableId);
  if (!removed) {
    throw new Error(`Lookup table '${input.tableId}' not found`);
  }
  // Reject if any LookupParameter still references this table. The user
  // must first detach the parameter (or re-link it to another table)
  // before deleting — matches AutoCAD's semantics where BLOOKUPTABLE
  // refuses to remove an in-use table and surfaces a dialog error.
  const referencing: string[] = [];
  for (const p of meta.parameters) {
    if (p.kind === "lookup") {
      const lp = p as LookupParameter;
      if (lp.lookupTableId === input.tableId) {
        referencing.push(lp.name || lp.id);
      }
    }
  }
  if (referencing.length > 0) {
    throw new Error(
      `Lookup table '${removed.name || removed.id}' is referenced by parameter(s): ${referencing.join(", ")}`,
    );
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    lookupTables: meta.lookupTables.filter((t) => t.id !== input.tableId),
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Removed lookup table '${removed.name || removed.id}' from block '${input.blockName}'`,
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Block Properties Table (BTABLE)
// ═══════════════════════════════════════════════════════════════════════

export interface SetBlockPropertiesTableInput {
  document: DrawingDocument;
  blockName: string;
  table: BlockPropertiesTable;
}

/**
 * Replace the entire block properties table. Pass `{ entries: [] }` to
 * clear it without removing the object (AutoCAD keeps the BTABLE shell
 * around so future additions keep the same GUID).
 */
export function setBlockPropertiesTableMutation(
  input: SetBlockPropertiesTableInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  // Guard key uniqueness inside the table.
  const seen = new Set<string>();
  for (const entry of input.table.entries) {
    if (seen.has(entry.key)) {
      throw new Error(
        `Block properties table has duplicate key '${entry.key}'`,
      );
    }
    seen.add(entry.key);
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    blockPropertiesTable: input.table,
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Set block properties table on block '${input.blockName}' (${input.table.entries.length} entr${
      input.table.entries.length === 1 ? "y" : "ies"
    })`,
  );
}

export interface AddBlockPropertyInput {
  document: DrawingDocument;
  blockName: string;
  entry: BlockPropertyEntry;
}

export function addBlockPropertyMutation(
  input: AddBlockPropertyInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.blockPropertiesTable ?? { entries: [] };
  if (existing.entries.some((e) => e.key === input.entry.key)) {
    throw new Error(`Block property key '${input.entry.key}' already exists`);
  }
  const nextTable: BlockPropertiesTable = {
    entries: [...existing.entries, input.entry],
  };
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    blockPropertiesTable: nextTable,
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Added block property '${input.entry.key}' to block '${input.blockName}'`,
  );
}

export interface UpdateBlockPropertyInput {
  document: DrawingDocument;
  blockName: string;
  key: string;
  updates: Partial<BlockPropertyEntry>;
}

export function updateBlockPropertyMutation(
  input: UpdateBlockPropertyInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.blockPropertiesTable;
  if (!existing) {
    throw new Error(`Block '${input.blockName}' has no properties table`);
  }
  const entry = existing.entries.find((e) => e.key === input.key);
  if (!entry) {
    throw new Error(`Block property '${input.key}' not found`);
  }
  const merged: BlockPropertyEntry = {
    ...entry,
    ...input.updates,
    // Key change is allowed but must be unique across remaining entries.
    key: input.updates.key ?? entry.key,
  };
  if (
    merged.key !== entry.key &&
    existing.entries.some((e) => e.key !== entry.key && e.key === merged.key)
  ) {
    throw new Error(`Block property key '${merged.key}' already exists`);
  }
  const nextTable: BlockPropertiesTable = {
    entries: existing.entries.map((e) => (e.key === input.key ? merged : e)),
  };
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    blockPropertiesTable: nextTable,
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Updated block property '${merged.key}' in block '${input.blockName}'`,
  );
}

export interface RemoveBlockPropertyInput {
  document: DrawingDocument;
  blockName: string;
  key: string;
}

export function removeBlockPropertyMutation(
  input: RemoveBlockPropertyInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.blockPropertiesTable;
  if (!existing) {
    throw new Error(`Block '${input.blockName}' has no properties table`);
  }
  if (!existing.entries.some((e) => e.key === input.key)) {
    throw new Error(`Block property '${input.key}' not found`);
  }
  const nextTable: BlockPropertiesTable = {
    entries: existing.entries.filter((e) => e.key !== input.key),
  };
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    blockPropertiesTable: nextTable,
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Removed block property '${input.key}' from block '${input.blockName}'`,
  );
}

export interface ReorderBlockPropertiesInput {
  document: DrawingDocument;
  blockName: string;
  orderedKeys: string[];
}

export function reorderBlockPropertiesMutation(
  input: ReorderBlockPropertiesInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.blockPropertiesTable;
  if (!existing) {
    throw new Error(`Block '${input.blockName}' has no properties table`);
  }
  if (input.orderedKeys.length !== existing.entries.length) {
    throw new Error(
      `Reorder must list every property (${existing.entries.length} total, got ${input.orderedKeys.length})`,
    );
  }
  const presentKeys = new Set(existing.entries.map((e) => e.key));
  for (const key of input.orderedKeys) {
    if (!presentKeys.has(key)) {
      throw new Error(`Reorder references unknown property key '${key}'`);
    }
  }
  const byKey = new Map(existing.entries.map((e) => [e.key, e] as const));
  const nextEntries = input.orderedKeys.map((key) => {
    const e = byKey.get(key);
    if (!e) throw new Error(`Property '${key}' disappeared during reorder`);
    return e;
  });
  const nextTable: BlockPropertiesTable = { entries: nextEntries };
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    blockPropertiesTable: nextTable,
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Reordered block properties on block '${input.blockName}'`,
  );
}
